import {useEffect, useRef, useState} from 'react'

/**
 * Fixed duration (ms) for every collapse/expand animation in agent-sessions.
 * Kept as a single source of truth so the JS unmount delay and the CSS
 * transition stay in lockstep.
 */
export const COLLAPSE_ANIMATION_DURATION_MS = 150

interface UseCollapseAnimationOptions {
  /**
   * When true the children stay mounted while collapsed (e.g. Session must keep
   * polling its logs). When false the children are unmounted once the collapse
   * animation finishes, keeping the resting DOM footprint identical to an
   * un-animated `{open && children}`.
   */
  keepMounted?: boolean
  durationMs?: number
}

interface CollapseAnimationState {
  /** Whether the children should be rendered. */
  shouldRender: boolean
  /** Whether the region is in its expanded visual state (drives the CSS class). */
  isExpanded: boolean
}

/**
 * Drives a fixed-duration grid-rows collapse/expand animation.
 *
 * Implemented as a small guarded state machine: a single timer and a single
 * rAF handle are tracked in refs and cleared before every transition, so rapid
 * open/close toggling can never strand a timer or unmount content out from
 * under an expanded region. Because the duration is fixed we rely on a timeout
 * rather than a `transitionend` listener, avoiding the missed-event races that
 * make variable-duration collapse animations flaky.
 */
export function useCollapseAnimation(
  open: boolean,
  {keepMounted = false, durationMs = COLLAPSE_ANIMATION_DURATION_MS}: UseCollapseAnimationOptions = {},
): CollapseAnimationState {
  const [shouldRender, setShouldRender] = useState(keepMounted || open)
  const [isExpanded, setIsExpanded] = useState(open)
  const [prevOpen, setPrevOpen] = useState(open)

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const rafRef = useRef<number | null>(null)
  const openRef = useRef(open)

  // Synchronous half of the transition, handled during render (rather than in an
  // effect) so we never call setState synchronously inside an effect body:
  //   - expanding mounts the content immediately, but stays visually collapsed
  //     so the rAF below can run the 0fr -> 1fr transition;
  //   - collapsing flips to the collapsed visual state right away.
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      if (!shouldRender) setShouldRender(true)
    } else if (isExpanded) {
      setIsExpanded(false)
    }
  }

  useEffect(() => {
    const clearPending = () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }

    const wasOpen = openRef.current
    openRef.current = open

    // Any new toggle cancels whatever the previous one had scheduled (see the
    // hook's JSDoc for why this guard matters).
    clearPending()

    if (open) {
      // Flip to the expanded state two frames out. A single rAF fires before the
      // browser paints, so the 0fr state and the 1fr flip would collapse into one
      // layout pass and skip the transition; the second rAF waits until 0fr has
      // actually painted, giving grid-rows a real starting point to animate from.
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          setIsExpanded(true)
        })
      })
    } else if (wasOpen && !keepMounted) {
      // Only schedule an unmount when we are actually collapsing from an open
      // state. (The initial collapsed mount has nothing to unmount, so it must
      // not schedule a timer that could later race with an expand.)
      timerRef.current = setTimeout(() => {
        setShouldRender(false)
        timerRef.current = null
      }, durationMs)
    }

    return clearPending
  }, [open, keepMounted, durationMs])

  return {shouldRender, isExpanded}
}
